import { Chip, Tooltip, Box } from '@mui/material'
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import { useQuery } from '@tanstack/react-query'

import { api } from '../lib/api'

interface Props {
  customerId: string | null | undefined
  /** When omitted (catalog rows), null renders "Shared". When the
   *  caller wants null to read as "—" instead (e.g., site list where
   *  customer is always required), pass `requiredNullLabel`. */
  requiredNullLabel?: string
  size?: 'small' | 'medium'
}

/// Compact chip showing which customer a catalog row is scoped to.
///   • `customer_id === null` → "Shared" with the infinity icon
///   • otherwise → the customer's name with a building icon
///
/// Self-hides for single-customer accounts so the column doesn't add
/// noise (there's only one customer; everything is implicitly scoped).
export function CustomerScopeChip({ customerId, requiredNullLabel, size = 'small' }: Props) {
  const customersQ = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list(),
    staleTime: 5 * 60_000,
  })
  const customers = customersQ.data ?? []

  if (customersQ.isLoading) return null
  // Single-customer org: nothing to disambiguate, skip the chip.
  if (customers.length <= 1) return null

  if (!customerId) {
    return (
      <Tooltip title="Available to every customer in your organization" arrow>
        <Chip
          size={size}
          icon={<AllInclusiveRoundedIcon sx={{ fontSize: 12 }} />}
          label={requiredNullLabel ?? 'Shared'}
          sx={{
            height: 20, fontSize: 10.5, fontWeight: 700,
            bgcolor: (t) => t.palette.mode === 'light' ? '#F1F5F9' : 'rgba(255,255,255,0.06)',
            color: 'text.secondary',
            '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
          }}
        />
      </Tooltip>
    )
  }

  const c = customers.find((x) => x.id === customerId)
  return (
    <Tooltip title={`Scoped to ${c?.name ?? 'this customer'}`} arrow>
      <Chip
        size={size}
        icon={<BusinessOutlinedIcon sx={{ fontSize: 12 }} />}
        label={<Box component="span" sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c?.name ?? '—'}</Box>}
        sx={{
          height: 20, fontSize: 10.5, fontWeight: 700,
          bgcolor: (t) => `${t.palette.primary.main}14`,
          color: 'primary.main',
          '& .MuiChip-icon': { ml: 0.5, mr: -0.25, color: 'primary.main' },
        }}
      />
    </Tooltip>
  )
}
